import { loadAccessToken, resetAccessToken } from "../stores/authStore";
import { getMessengerOpenRoomId } from "../stores/messengerOpenRoomStore";
import { setTotalUnreadCount } from "../stores/totalUnreadStore";
import { getCurrentUserId, getPreviewTextFromLastMessage } from "../utils/messageMapping";
import { getChatWebSocketUrl } from "./getChatWebSocketUrl";
import { WS_ERROR, WS_WARN } from "./webSocketMessages";
import { sendAuthRequest, sendTotalUnreadCountRequest } from "./webSocketRequests";
import { WS_DISCONNECT_REASON, WS_MESSAGE_TYPE } from "./webSocketType";

let socket = null;
let opening = false;

let manualDisconnect = false;

let reconnectTimer = null;

let reconnectPolicy = "infinite";
let limitedReconnectAttempts = 0;
let infiniteReconnectAttempt = 0;

const LIMITED_RECONNECT_MAX = 3;
const BACKOFF_BASE_MS = 1000;
const BACKOFF_MAX_MS = 30000;
const RECONNECT_JITTER_MS = 400;
const AUTH_FAILURE_CLOSE_DELAY_MS = 10000;

/** 소켓이 끊겼다가 다시 AUTH.SUCCESS 난 뒤 — 열린 방 히스토리 재요청용 */
export const CHAT_WS_RECONNECTED_EVENT = "chatWebSocketReconnected";

let notifyReconnectAfterAuth = false;

function clearReconnectTimer() {
  if (reconnectTimer != null) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function computeBackoffMs(attemptIndex) {
  const exp = Math.min(Math.max(attemptIndex, 1), 10);
  const raw = BACKOFF_BASE_MS * 2 ** (exp - 1);
  const capped = Math.min(BACKOFF_MAX_MS, raw);
  const jitter = Math.floor(Math.random() * RECONNECT_JITTER_MS);
  return capped + jitter;
}


function scheduleReconnectAfterClose() {
  clearReconnectTimer();
  if (manualDisconnect) return;

  if (reconnectPolicy === "limited") {
    limitedReconnectAttempts += 1;
    if (limitedReconnectAttempts > LIMITED_RECONNECT_MAX) {
      console.warn("[chatWebSocket] 재연결 횟수 초과");
      return;
    }
    const delay = computeBackoffMs(limitedReconnectAttempts);
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      openChatWebSocketConnection();
    }, delay);
    return;
  }

  infiniteReconnectAttempt += 1;
  const delay = computeBackoffMs(infiniteReconnectAttempt);
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    openChatWebSocketConnection();
  }, delay);
}

function attachHandlers(ws, token) {
  const messageHandlers = {
    [WS_MESSAGE_TYPE.AUTH_SUCCESS]: () => {
      reconnectPolicy = "infinite";
      limitedReconnectAttempts = 0;
      infiniteReconnectAttempt = 0;
      sendTotalUnreadCountRequest(ws);
      const shouldNotifyReconnect = notifyReconnectAfterAuth;
      notifyReconnectAfterAuth = false;
      if (shouldNotifyReconnect && typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(CHAT_WS_RECONNECTED_EVENT));
      }
    },
    [WS_MESSAGE_TYPE.USER_TOTAL_UNREAD_COUNT_ACK]: (data) => {
      const p = data.payload ?? {};
      setTotalUnreadCount(p.totalUnreadCount ?? p.total_unread_count);
    },
    [WS_MESSAGE_TYPE.ROOM_CREATED]: (data) => {
      const room = data.payload?.room;
      if (!room?.id) return;
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("chatRoomCreated", { detail: { room } }));
      }
    },
    [WS_MESSAGE_TYPE.AUTH_FAILURE]: () => {
      console.warn(WS_WARN.AUTH_FAILURE);
      reconnectPolicy = "limited";
      setTimeout(() => {
        resetAccessToken();
        try {
          if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
            ws.close();
          }
        } catch {}
      }, AUTH_FAILURE_CLOSE_DELAY_MS);
    },
    [WS_MESSAGE_TYPE.USER_TOTAL_UNREAD_COUNT_NACK]: (data) => {
      const { code, message, retryable } = data.payload ?? {};
      console.warn(WS_WARN.TOTAL_UNREAD_COUNT_FAIL, { code, message, retryable });
    },
    [WS_MESSAGE_TYPE.USER_BLOCK_UPDATED]: (data) => {
      const { code, userId } = data.payload ?? {};
      if (!code || !userId) return;
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("userBlockUpdated", { detail: { code, userId } }));
      }
    },
    [WS_MESSAGE_TYPE.MESSAGE_HISTORY_ACK]: (data) => {
      const { roomId, messages, nextCursor, hasMore } = data.payload ?? {};
      if (roomId == null) return;
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("messageHistoryAck", {
            detail: { roomId, messages: messages ?? [], nextCursor, hasMore: !!hasMore },
          })
        );
      }
    },
    [WS_MESSAGE_TYPE.MESSAGE_HISTORY_NACK]: (data) => {
      const { code, message, retryable } = data.payload ?? {};
      console.warn(WS_WARN.MESSAGE_HISTORY_FAIL, { code, message, retryable });
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("messageHistoryNack", { detail: { code, message, retryable } })
        );
      }
    },
    [WS_MESSAGE_TYPE.MESSAGE_ACK]: (data) => {
      const message = data.payload?.message;
      const meta = data.meta ?? {};
      if (!message) return;
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("messageAck", { detail: { message, meta } }));
      }
    },
    [WS_MESSAGE_TYPE.MESSAGE_NACK]: (data) => {
      const { payload, meta } = data ?? {};
      console.warn(WS_WARN.MESSAGE_SEND_FAIL, { payload, meta });
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("messageNack", { detail: { payload, meta } }));
      }
    },
    [WS_MESSAGE_TYPE.MESSAGE_RECEIVED]: (data) => {
      const message = data.payload?.message;
      const meta = data.meta ?? {};
      if (!message?.messageId) return;
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("messageReceived", { detail: { message, meta } }));
      }
      // 탭이 비활성일 때 브라우저 알림
      if (typeof document !== "undefined" && document.hidden && typeof Notification !== "undefined" && Notification.permission === "granted") {
        const senderName = message.senderName || "채팅";
        const body = getPreviewTextFromLastMessage(message) || "새 메시지가 도착했습니다";
        const n = new Notification(`디스코 - ${senderName}`, { body, tag: `chat-${message.messageId}` });
        n.onclick = () => { window.focus(); n.close(); };
      }
      const openRoomId = getMessengerOpenRoomId();
      if (openRoomId && message.roomId === openRoomId) return;
      sendTotalUnreadCountRequest(ws);
    },
    [WS_MESSAGE_TYPE.ROOM_READ_RECEIPT_ACK]: () => {
      sendTotalUnreadCountRequest(ws);
    },
    [WS_MESSAGE_TYPE.ROOM_READ_RECEIPT_UPDATED]: (data) => {
      const p = data.payload ?? {};
      const roomId = p.roomId;
      const userId = p.userId ?? data.meta?.userId;
      const lastReadSeq = p.lastReadSeq;
      if (typeof window !== "undefined" && roomId && userId) {
        window.dispatchEvent(
          new CustomEvent("roomReadReceiptUpdated", {
            detail: {
              roomId,
              userId,
              lastReadSeq: typeof lastReadSeq === "number" ? lastReadSeq : 0,
            },
          })
        );
      }
      const actorId = userId;
      const me = getCurrentUserId();
      if (!me || actorId !== me) return;
      sendTotalUnreadCountRequest(ws);
    },
    [WS_MESSAGE_TYPE.ROOM_READ_RECEIPT_NACK]: (data) => {
      const { code, message, retryable } = data.payload ?? {};
      console.warn("[chatWebSocket] 읽음 처리 실패", { code, message, retryable });
    },
    [WS_MESSAGE_TYPE.ROOM_LEFT]: (data) => {
      const roomId = data.payload?.roomId;
      const userId = data.meta?.userId;
      if (typeof window !== "undefined" && roomId) {
        window.dispatchEvent(new CustomEvent("roomLeft", { detail: { roomId, userId } }));
      }
    },
    [WS_MESSAGE_TYPE.SYSTEM_DISCONNECT]: (data) => {
      const { reason, message } = data.payload ?? {};
      console.warn("[chatWebSocket] SYSTEM.DISCONNECT", { reason, message });
      reconnectPolicy = "infinite";
      if (reason === WS_DISCONNECT_REASON.TOKEN_EXPIRED) {
        resetAccessToken();
      }
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("messageNack"));
      }
    },
    [WS_MESSAGE_TYPE.SYSTEM_LOGOUT]: (data) => {
      const { reason, message } = data.payload ?? {};
      console.warn("[chatWebSocket] SYSTEM.LOGOUT", { reason, message });
      reconnectPolicy = "limited";
      resetAccessToken();
    },
    [WS_MESSAGE_TYPE.SYSTEM_ERROR]: (data) => {
      const { code, message, retryable } = data.payload ?? {};
      console.warn("[chatWebSocket] SYSTEM.ERROR", { code, message, retryable });
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("messageNack"));
      }
    },
  };

  ws.onopen = () => {
    sendAuthRequest(ws, token);
    console.log("[chatWebSocket] 연결됨");
  };

  ws.onclose = (event) => {
    console.log("[chatWebSocket] 연결 종료", event.code, event.reason);
    if (socket === ws) socket = null;

    if (manualDisconnect) {
      manualDisconnect = false;
      reconnectPolicy = "infinite";
      limitedReconnectAttempts = 0;
      infiniteReconnectAttempt = 0;
      clearReconnectTimer();
      return;
    }

    notifyReconnectAfterAuth = true;
    scheduleReconnectAfterClose();
  };

  ws.onerror = (error) => {
    console.error(WS_ERROR.CONNECTION, error);
  };

  ws.onmessage = (event) => {
    try {
      const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      const handler = messageHandlers[data.type];
      if (handler) handler(data);
    } catch (e) {
      console.warn(WS_WARN.MESSAGE_PARSE_FAIL, event.data);
    }
  };
}

async function openChatWebSocketConnection() {
  if (manualDisconnect) return;
  if (socket?.readyState === WebSocket.OPEN) return;
  if (socket?.readyState === WebSocket.CONNECTING) return;
  if (opening) return;

  opening = true;
  try {
    const token = await loadAccessToken();
    if (!token) {
      console.warn(WS_WARN.NO_TOKEN);
      const mayRetry =
        infiniteReconnectAttempt > 0 ||
        (limitedReconnectAttempts > 0 && limitedReconnectAttempts <= LIMITED_RECONNECT_MAX);
      if (mayRetry) {
        scheduleReconnectAfterClose();
      }
      return;
    }

    const url = getChatWebSocketUrl();
    const ws = new WebSocket(url);
    socket = ws;
    attachHandlers(ws, token);
  } finally {
    opening = false;
  }
}

export async function connectChatWebSocket() {
  manualDisconnect = false;
  clearReconnectTimer();
  if (socket?.readyState === WebSocket.OPEN) return;
  if (socket?.readyState === WebSocket.CONNECTING) return;
  limitedReconnectAttempts = 0;
  infiniteReconnectAttempt = 0;
  reconnectPolicy = "infinite";
  await openChatWebSocketConnection();
}

// 탭/앱 전환 시 WS 관리
if (typeof document !== "undefined") {
  const isMobileApp = /android|iphone|ipad|ipod/i.test(navigator.userAgent);

  document.addEventListener("visibilitychange", () => {
    if (manualDisconnect) return;

    if (document.hidden) {
      // 모바일 앱 백그라운드 → WS 끊기 (서버가 push 발송하도록)
      if (isMobileApp && socket) {
        clearReconnectTimer();
        socket.close();
        socket = null;
      }
      return;
    }

    // 포그라운드 복귀 → 재연결
    if (socket?.readyState === WebSocket.OPEN) return;
    clearReconnectTimer();
    infiniteReconnectAttempt = 0;
    openChatWebSocketConnection();
  });
}

export function getChatSocket() {
  return socket?.readyState === WebSocket.OPEN ? socket : null;
}

/** 목록으로 나가기·채팅방 나가기 직후 등 — 호스트 배지용 전체 unread 재조회 (WS 연결 시만) */
export function requestChatTotalUnreadRefresh() {
  if (socket?.readyState === WebSocket.OPEN) {
    sendTotalUnreadCountRequest(socket);
  }
}

export function disconnectChatWebSocket() {
  manualDisconnect = true;
  clearReconnectTimer();
  reconnectPolicy = "infinite";
  limitedReconnectAttempts = 0;
  infiniteReconnectAttempt = 0;
  if (socket) {
    socket.close();
    socket = null;
  }
}
